import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
  Badge,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  Table,
  Tbody,
  Tr,
  Td,
  TableContainer,
  Avatar,
  Divider,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiMoreVertical } from "react-icons/fi";
import { MdEmail, MdPhone } from "react-icons/md";
import { FaWhatsapp, FaUserFriends } from "react-icons/fa";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  fetchOneCustomerSegment,
  deleteOneCustomerSegment,
} from "../../../actions/management.action";
import { LogoutAlertDialog } from "../../../components/LogoutAlertDialog";
import { Loading } from "../../../components/Loading";

const CustomerDetails = () => {
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const onCloseAlert = () => setIsAlertOpen(false);
  const onOpenAlert = () => setIsAlertOpen(true);
  const isValidObjectId = (value) => /^[0-9a-fA-F]{24}$/.test(value || "");

  const getCustomerDetails = async () => {
    try {
      let res = await fetchOneCustomerSegment(id);
      setCustomerData(res.data);
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteCustomer = async () => {
    if (!window.confirm("Are you sure you want to delete this customer?"))
      return;
    setIsDeleting(true);
    try {
      await deleteOneCustomerSegment(id);
      toast({
        title: "Success",
        description: "Customer deleted successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate("/management/customer");
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: error.message || "Unable to delete customer",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      onCloseAlert();
    }
  };

  useEffect(() => {
    if (!id) return;

    if (!isValidObjectId(id)) {
      setLoading(false);
      toast({
        title: "Invalid customer",
        description: "The selected customer link is invalid.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      navigate("/management/customer");
      return;
    }

    getCustomerDetails();
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  if (!customerData) {
    return (
      <Box px={4} py={6}>
        <Text>Customer not found</Text>
      </Box>
    );
  }

  return (
    <Box px={4} py={6}>
      <Flex
        justifyContent={{ base: "center", md: "space-between" }}
        alignItems={{ base: "flex-start", md: "center" }}
        mb={6}
        flexDir={{ base: "column", md: "row" }}
      >
        <BreadCrumbMaker
          items={[
            { link: "/management", title: "Operations Setup" },
            { link: "/management/customer", title: "Customers" },
            {
              link: `/management/customer/${id}`,
              title: customerData?.name || "Details",
              on: true,
            },
          ]}
        />

        <Flex gap={2}>
          <Button
            as={Link}
            to={`/management/customer/edit/${id}`}
            colorScheme="blue"
            size="sm"
            borderRadius={5}
            px={10}
            fontWeight={0}
            py={5}
          >
            Edit Customer
          </Button>
        </Flex>
      </Flex>

      {/* Content Section */}
      <Box p={4} bg={"white"} overflowX={"auto"} borderRadius={"lg"}>
        {/* Main Customer Info Card */}
        <Box
          border="2px"
          borderColor="gray.200"
          p={4}
          shadow="sm"
          bg={"white"}
          borderRadius={"lg"}
          mb={4}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            borderBottomWidth={{ base: "0", md: "2px" }}
            borderBottomColor={{ base: "transparent", md: "gray.200" }}
            pb={4}
          >
            <Flex
              gap={4}
              flexDirection={{ base: "column", md: "row" }}
              mb={4}
              alignItems={{ base: "stretch", md: "center" }}
            >
              <Avatar
                name={customerData?.name}
                size="2xl"
                bg="blue.500"
                color="white"
              />
              <VStack alignItems={"stretch"} spacing={3}>
                <Heading fontSize="2xl" mb={1} color={"#1A202C"}>
                  {customerData?.name}
                </Heading>
                <Text fontSize="sm" color={"gray.600"} mb={2}>
                  Customer ID: {customerData?.customerID}
                </Text>
                <Flex
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  gap={3}
                  flexWrap="wrap"
                >
                  <Badge
                    variant="subtle"
                    colorScheme={
                      customerData?.status === "Active" ? "green" : "red"
                    }
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    {customerData?.status}
                  </Badge>
                  <Badge
                    variant="subtle"
                    colorScheme={customerData?.isCorporate ? "purple" : "blue"}
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    {customerData?.type}
                  </Badge>
                  <Badge
                    variant="subtle"
                    colorScheme="orange"
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    {customerData?.accountCategory}
                  </Badge>
                  {customerData?.rating > 0 && (
                    <Badge
                      variant="subtle"
                      colorScheme="yellow"
                      fontSize="sm"
                      px={3}
                      py={1}
                    >
                      ⭐ {customerData?.rating}
                    </Badge>
                  )}
                </Flex>
              </VStack>
            </Flex>

            {/* Action Button */}
            <Menu>
              <MenuButton
                as={Button}
                aria-label="More Options"
                variant="ghost"
                size="sm"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
              >
                <FiMoreVertical />
              </MenuButton>
              <MenuList
                minW="150px"
                p={2}
                gap={2}
                borderRadius={0}
                boxShadow={"lg"}
              >
                <Link
                  to={`/management/customer/edit/${id}`}
                  style={{ textDecoration: "none" }}
                >
                  <Box
                    _hover={{
                      bg: "gray.100",
                      color: "blue.500",
                      cursor: "pointer",
                    }}
                    p={2}
                    borderRadius={0}
                    transition="background-color 0.2s ease"
                  >
                    Edit
                  </Box>
                </Link>
                <Box
                  _hover={{
                    bg: "gray.100",
                    color: "red.500",
                    cursor: "pointer",
                  }}
                  p={2}
                  borderRadius={0}
                  transition="background-color 0.2s ease"
                >
                  <LogoutAlertDialog
                    isOpen={isAlertOpen}
                    onOpen={onOpenAlert}
                    callback={deleteCustomer}
                    onClose={onCloseAlert}
                    title={"Delete Customer"}
                    heading={"Are you sure you want to delete this customer?"}
                    buttonViewContent={<Text>Delete</Text>}
                    isLoading={isDeleting}
                  />
                </Box>
              </MenuList>
            </Menu>
          </Flex>

          {/* Basic Statistics */}
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={4}
            mt={4}
          >
            <Box
              p={4}
              bg="blue.50"
              borderRadius="md"
              border="1px"
              borderColor="blue.200"
            >
              <Text fontSize="sm" color="gray.600" mb={1}>
                Total Trips
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                {customerData?.noOfTrips || 0}
              </Text>
            </Box>
            <Box
              p={4}
              bg="purple.50"
              borderRadius="md"
              border="1px"
              borderColor="purple.200"
            >
              <Text fontSize="sm" color="gray.600" mb={1}>
                Passengers
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="purple.600">
                {customerData?.passengers?.length || 0}
              </Text>
            </Box>
            <Box
              p={4}
              bg="orange.50"
              borderRadius="md"
              border="1px"
              borderColor="orange.200"
            >
              <Text fontSize="sm" color="gray.600" mb={1}>
                Emergency Contacts
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                {customerData?.emergencyContacts?.length || 0}
              </Text>
            </Box>
          </Grid>
        </Box>

        {/* Contact Information Section */}
        <Box
          border="2px"
          borderColor="gray.200"
          p={4}
          shadow="sm"
          bg={"white"}
          borderRadius={"lg"}
          mb={4}
        >
          <Heading fontSize="md" mb={4} color={"#1A202C"}>
            Contact Information
          </Heading>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
            <Flex
              alignItems="center"
              gap={3}
              p={3}
              bg="gray.50"
              borderRadius="md"
            >
              <MdEmail size={24} color="#4299E1" />
              <Box>
                <Text fontSize="xs" color="gray.600" mb={1}>
                  Email
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {customerData?.email}
                </Text>
              </Box>
            </Flex>
            <Flex
              alignItems="center"
              gap={3}
              p={3}
              bg="gray.50"
              borderRadius="md"
            >
              <MdPhone size={24} color="#48BB78" />
              <Box>
                <Text fontSize="xs" color="gray.600" mb={1}>
                  Mobile
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {customerData?.mobile}
                </Text>
              </Box>
            </Flex>
            <Flex
              alignItems="center"
              gap={3}
              p={3}
              bg="gray.50"
              borderRadius="md"
            >
              <FaWhatsapp size={24} color="#25D366" />
              <Box>
                <Text fontSize="xs" color="gray.600" mb={1}>
                  WhatsApp
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {customerData?.whatsapp}
                </Text>
              </Box>
            </Flex>
            <Flex
              alignItems="center"
              gap={3}
              p={3}
              bg="gray.50"
              borderRadius="md"
            >
              <FaUserFriends size={24} color="#805AD5" />
              <Box>
                <Text fontSize="xs" color="gray.600" mb={1}>
                  Preferred Channels
                </Text>
                <Text fontSize="sm" fontWeight="medium">
                  {customerData?.preferredChannels?.length > 0
                    ? customerData?.preferredChannels?.join(", ")
                    : "Not specified"}
                </Text>
              </Box>
            </Flex>
          </Grid>
        </Box>

        {/* Personal Details Section */}
        <Box
          border="2px"
          borderColor="gray.200"
          p={4}
          shadow="sm"
          bg={"white"}
          borderRadius={"lg"}
          mb={4}
        >
          <Heading fontSize="md" mb={4} color={"#1A202C"}>
            Personal Details
          </Heading>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Tbody>
                <Tr>
                  <Td fontWeight="bold" width="30%">
                    City
                  </Td>
                  <Td>{customerData?.city}</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Gender</Td>
                  <Td>{customerData?.gender}</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Languages</Td>
                  <Td>{customerData?.language}</Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Account Created</Td>
                  <Td>
                    {customerData?.createdAt
                      ? new Date(customerData?.createdAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                      : "N/A"}
                  </Td>
                </Tr>
                <Tr>
                  <Td fontWeight="bold">Last Updated</Td>
                  <Td>
                    {customerData?.updatedAt
                      ? new Date(customerData?.updatedAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                      : "N/A"}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* Corporate Details Section - Only show if corporate customer */}
        {customerData?.isCorporate && customerData?.corporateDetails && (
          <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={4}
          >
            <Heading fontSize="md" mb={4} color={"#1A202C"}>
              Corporate Details
            </Heading>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Tbody>
                  <Tr>
                    <Td fontWeight="bold" width="30%">
                      Company Name
                    </Td>
                    <Td>{customerData?.corporateDetails?.companyName}</Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">GST Number</Td>
                    <Td>{customerData?.corporateDetails?.gstNo}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Passengers Section */}
        {customerData?.passengers?.length > 0 && (
          <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={4}
          >
            <Heading fontSize="md" mb={4} color={"#1A202C"}>
              Passengers ({customerData?.passengers?.length})
            </Heading>
            <Grid gap={4}>
              {customerData?.passengers?.map((passenger, index) => (
                <Box
                  key={passenger._id || index}
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  border="1px"
                  borderColor="gray.200"
                >
                  <Flex alignItems="center" gap={3} mb={3}>
                    <Avatar
                      name={passenger?.name}
                      size="md"
                      bg="purple.500"
                      color="white"
                    />
                    <Box>
                      <Text fontWeight="bold" fontSize="md">
                        {passenger?.name}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Passenger #{index + 1}
                      </Text>
                    </Box>
                  </Flex>
                  <Divider mb={3} />
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                    gap={3}
                  >
                    <Box>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Email
                      </Text>
                      <Text fontSize="sm">{passenger?.email}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Mobile
                      </Text>
                      <Text fontSize="sm">{passenger?.mobile}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        WhatsApp
                      </Text>
                      <Text fontSize="sm">{passenger?.whatsapp}</Text>
                    </Box>
                  </Grid>
                </Box>
              ))}
            </Grid>
          </Box>
        )}

        {/* Emergency Contacts Section */}
        {customerData?.emergencyContacts?.length > 0 && (
          <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={4}
          >
            <Heading fontSize="md" mb={4} color={"#1A202C"}>
              Emergency Contacts ({customerData?.emergencyContacts?.length})
            </Heading>
            <Grid gap={4}>
              {customerData?.emergencyContacts?.map((contact, index) => (
                <Box
                  key={contact._id || index}
                  p={4}
                  bg="red.50"
                  borderRadius="md"
                  border="1px"
                  borderColor="red.200"
                >
                  <Text fontWeight="bold" fontSize="md" mb={3}>
                    Contact #{index + 1}
                  </Text>
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={3}
                  >
                    <Box>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Name
                      </Text>
                      <Text fontSize="sm">{contact?.name}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Relationship
                      </Text>
                      <Text fontSize="sm">{contact?.relationship}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        Phone
                      </Text>
                      <Text fontSize="sm">{contact?.phone}</Text>
                    </Box>
                  </Grid>
                </Box>
              ))}
            </Grid>
          </Box>
        )}

        {/* Comments & Notes Section */}
        {(customerData?.comments || customerData?.notes) && (
          <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
            mb={4}
          >
            <Heading fontSize="md" mb={4} color={"#1A202C"}>
              Comments & Notes
            </Heading>
            <VStack alignItems="stretch" spacing={3}>
              {customerData?.comments && customerData?.comments !== "NA" && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" mb={2} color="gray.700">
                    Comments
                  </Text>
                  <Text
                    fontSize="sm"
                    p={3}
                    bg="yellow.50"
                    borderRadius="md"
                    border="1px"
                    borderColor="yellow.200"
                  >
                    {customerData?.comments}
                  </Text>
                </Box>
              )}
              {customerData?.notes && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" mb={2} color="gray.700">
                    Notes
                  </Text>
                  <Text
                    fontSize="sm"
                    p={3}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px"
                    borderColor="blue.200"
                  >
                    {customerData?.notes}
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>
        )}

        {/* Communication Log Section */}
        {customerData?.communicationLog?.length > 0 && (
          <Box
            border="2px"
            borderColor="gray.200"
            p={4}
            shadow="sm"
            bg={"white"}
            borderRadius={"lg"}
          >
            <Heading fontSize="md" mb={4} color={"#1A202C"}>
              Communication Log
            </Heading>
            <Text fontSize="sm" color="gray.600">
              {customerData?.communicationLog?.length} communications recorded
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomerDetails;
